// UserGuideModal.tsx
import React, { useState } from "react";
import styles from "./UserGuideModal.module.css";
import createIconLight from "../../assets/create_dark.svg";
import createIconDark from "../../assets/create_light.svg";
import modifyIconLight from "../../assets/rename_dark.svg";
import modifyIconDark from "../../assets/rename_light.svg";
import deleteIconLight from "../../assets/delete_dark.svg";
import deleteIconDark from "../../assets/delete_light.svg";
import restoreIconLight from "../../assets/branch_dark.svg";
import restoreIconDark from "../../assets/branch_light.svg";
import closeIconLight from "../../assets/close_light.svg";
import closeIconDark from "../../assets/close_dark.svg";
import { useThemedIcon } from "../../hooks/useThemedIcon";

interface UserGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserGuideModal: React.FC<UserGuideModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<
    "operations" | "primitives" | "features"
  >("operations");

  if (!isOpen) return null;

  const { getIcon } = useThemedIcon();
  const createIcon = getIcon(createIconLight, createIconDark);
  const modifyIcon = getIcon(modifyIconLight, modifyIconDark);
  const deleteIcon = getIcon(deleteIconLight, deleteIconDark);
  const restoreIcon = getIcon(restoreIconLight, restoreIconDark);
  const closeIcon = getIcon(closeIconLight, closeIconDark);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const primitives = [
    { name: "Box", params: "Width × Height × Depth", example: "30x20x10 box" },
    {
      name: "Cylinder",
      params: "Radius, Height",
      example: "10mm radius, 20mm tall",
    },
    { name: "Sphere", params: "Radius", example: "15mm radius" },
    {
      name: "Cone",
      params: "Base radius, Top radius, Height",
      example: "10mm base, 5mm top, 20mm tall",
    },
    {
      name: "Torus",
      params: "Ring radius, Tube radius",
      example: "20mm ring, 5mm tube",
    },
    {
      name: "Wedge",
      params: "Width, Height, Depth, Top width",
      example: "30x20x10, top 15mm wide",
    },
    { name: "Ellipsoid", params: "X, Y, Z radii", example: "15x10x8 radii" },
    {
      name: "Prism",
      params: "Sides, Radius, Height",
      example: "hexagonal, 10mm radius, 20mm tall",
    },
  ];

  const features = [
    {
      name: "Pad",
      icon: modifyIcon,
      desc: "Add material to bodies",
      example: "Pad a 60x10 rectangle, 5mm high",
      warning: "Select or specify a face",
    },
    {
      name: "Pocket",
      icon: modifyIcon,
      desc: "Remove material from bodies",
      example: "Pocket a 20x10x5 rectangle",
      warning: "Select or specify a face",
      tip: "Use pockets to create wall thickness instead of the Thickness feature",
    },
    {
      name: "Thickness",
      icon: modifyIcon,
      desc: "Create hollow walls by removing a face",
      example: "Make this 2mm thick",
      warning: "Select or specify a face",
    },
    {
      name: "Draft",
      icon: modifyIcon,
      desc: "Taper faces at an angle",
      example: "Draft 5 degrees inward",
      warning: "Select or specify a face",
    },
    {
      name: "Fillet",
      icon: modifyIcon,
      desc: "Round edges with smooth curves",
      example: "Fillet this edge 1mm",
      warning: "Select an edge",
    },
    {
      name: "Chamfer",
      icon: modifyIcon,
      desc: "Bevel edges with flat cuts",
      example: "Chamfer this edge 2mm",
      warning: "Select an edge",
      tip: 'Create unequal chamfers: "Chamfer 2mm by 3mm"',
    },
  ];

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.title}>Quick Reference Guide</h2>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${
              activeTab === "operations" ? styles.tabActive : ""
            }`}
            onClick={() => setActiveTab("operations")}
          >
            Operations
          </button>
          <button
            className={`${styles.tab} ${
              activeTab === "primitives" ? styles.tabActive : ""
            }`}
            onClick={() => setActiveTab("primitives")}
          >
            Primitives
          </button>
          <button
            className={`${styles.tab} ${
              activeTab === "features" ? styles.tabActive : ""
            }`}
            onClick={() => setActiveTab("features")}
          >
            Features
          </button>
        </div>

        <div className={styles.modalBody}>
          {activeTab === "operations" && (
            <div className={styles.content}>
              <div className={styles.operationItem}>
                <div className={styles.operationBadge}>
                  <img
                    src={createIcon}
                    alt="Create"
                    className={styles.badgeIcon}
                  />
                  Create
                </div>
                <p className={styles.operationDesc}>
                  Start your project with a primitive shape.
                </p>
                <div className={styles.codeBlock}>
                  "Make a cylinder, 15mm diameter and 25mm tall"
                </div>
              </div>

              <div className={styles.operationItem}>
                <div className={styles.operationBadge}>
                  <img
                    src={modifyIcon}
                    alt="Edit"
                    className={styles.badgeIcon}
                  />
                  Edit
                </div>
                <p className={styles.operationDesc}>
                  Modify bodies or features by adding, subtracting, or adjusting
                  them.
                </p>
                <div className={styles.exampleList}>
                  <div className={styles.exampleItem}>
                    Add: "Pad a 20x10x5 rectangle"
                  </div>
                  <div className={styles.exampleItem}>
                    Subtract: "Pocket a 5mm circle, 5mm deep"
                  </div>
                  <div className={styles.exampleItem}>
                    Resize: "Make Pad001 60mm by 30mm by 10mm"
                  </div>
                  <div className={styles.exampleItem}>
                    Move: "Move Pocket001 10mm from the right edge"
                  </div>
                </div>
                <div className={styles.noteBox}>
                  <strong>Note:</strong> Always specify feature names when
                  editing (e.g., Pad001, Fillet002)
                </div>
              </div>

              <div className={styles.operationItem}>
                <div className={styles.operationBadge}>
                  <img
                    src={deleteIcon}
                    alt="Delete"
                    className={styles.badgeIcon}
                  />
                  Delete
                </div>
                <p className={styles.operationDesc}>
                  Remove a feature from the timeline.
                </p>
                <div className={styles.warningBox}>
                  <strong>Warning:</strong> This removes all features after it
                  in the timeline
                </div>
              </div>

              <div className={styles.operationItem}>
                <div className={styles.operationBadge}>
                  <img
                    src={restoreIcon}
                    alt="Branch"
                    className={styles.badgeIcon}
                  />
                  Branch
                </div>
                <p className={styles.operationDesc}>
                  Create a new version from any point in your project history.
                  Your original remains unchanged.
                </p>
              </div>
            </div>
          )}

          {activeTab === "primitives" && (
            <div className={styles.primitiveGrid}>
              {primitives.map((primitive) => (
                <div key={primitive.name} className={styles.primitiveCard}>
                  <h3 className={styles.primitiveTitle}>{primitive.name}</h3>
                  <p className={styles.primitiveParams}>{primitive.params}</p>
                  <div className={styles.codeBlock}>"{primitive.example}"</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "features" && (
            <div className={styles.content}>
              {features.map((feature) => (
                <div key={feature.name} className={styles.featureCard}>
                  <div className={styles.featureHeader}>
                    <img
                      src={feature.icon}
                      alt={feature.name}
                      className={styles.featureIcon}
                    />
                    <h3 className={styles.featureTitle}>{feature.name}</h3>
                  </div>
                  <p className={styles.featureDesc}>{feature.desc}</p>
                  <div className={styles.codeBlock}>"{feature.example}"</div>
                  {feature.warning && (
                    <div className={styles.warningBoxSmall}>
                      ⚠️ {feature.warning}
                    </div>
                  )}
                  {feature.tip && (
                    <div className={styles.tipBoxSmall}>💡 {feature.tip}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGuideModal;
